/* ============================================================
   Christian Catalogue — behaviour layer v2
   No dependencies. Every feature degrades to a working link
   or a native form if JS fails.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. Marquee: duplicate the track so the loop is seamless ---------- */
  function initMarquee(root) {
    root.querySelectorAll('[data-cx-marquee]').forEach(function (el) {
      if (el.dataset.cxMarqueeReady) return;
      el.dataset.cxMarqueeReady = '1';
      var track = el.querySelector('.cx-marquee__track');
      if (!track) return;
      // The CSS animation translates -50%, so we need exactly two copies.
      track.innerHTML += track.innerHTML;
    });
  }

  /* ---------- 2. Accordion (FAQ) ---------- */
  function initAccordion(root) {
    root.querySelectorAll('[data-cx-accordion]').forEach(function (group) {
      if (group.dataset.cxAccordionReady) return;
      group.dataset.cxAccordionReady = '1';
      var single = group.dataset.cxAccordion === 'single';

      group.querySelectorAll('.cx-faq__q').forEach(function (btn) {
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;

        if (btn.getAttribute('aria-expanded') === 'true') {
          panel.style.maxHeight = panel.scrollHeight + 'px';
        }

        btn.addEventListener('click', function () {
          var open = btn.getAttribute('aria-expanded') === 'true';

          if (single && !open) {
            group.querySelectorAll('.cx-faq__q[aria-expanded="true"]').forEach(function (other) {
              other.setAttribute('aria-expanded', 'false');
              var op = document.getElementById(other.getAttribute('aria-controls'));
              if (op) op.style.maxHeight = null;
            });
          }

          btn.setAttribute('aria-expanded', String(!open));
          panel.style.maxHeight = open ? null : panel.scrollHeight + 'px';
        });
      });
    });

    // Keep open panels correctly sized when the viewport reflows text.
    if (!window.__cxResizeBound) {
      window.__cxResizeBound = true;
      var t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          document.querySelectorAll('.cx-faq__q[aria-expanded="true"]').forEach(function (btn) {
            var p = document.getElementById(btn.getAttribute('aria-controls'));
            if (p) p.style.maxHeight = p.scrollHeight + 'px';
          });
        }, 150);
      });
    }
  }

  /* ---------- 3. Scroll reveal ---------- */
  function initReveal(root) {
    var targets = root.querySelectorAll('.cx-reveal:not([data-cx-reveal-ready])');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.dataset.cxRevealReady = '1';
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );

    targets.forEach(function (el) {
      el.dataset.cxRevealReady = '1';
      io.observe(el);
    });
  }

  /* ---------- 4. Product cards: swatches + size quick-add ---------- */

  // Card state lives in data attributes written by Liquid, so the DOM is
  // the single source of truth and the section can re-render safely.
  // Colour names are typed twice — once on the rail block, once on the product
  // option — so an exact === compare fails silently on "Gray" vs "Grey",
  // "Sky Blue" vs "sky blue", or a stray trailing space. Normalise both sides.
  var COLOUR_ALIASES = {
    grey: 'gray', heathergrey: 'gray', heathergray: 'gray',
    sand: 'beige', cream: 'beige', bone: 'beige', oatmeal: 'beige',
    sky: 'skyblue', lightblue: 'skyblue', babyblue: 'skyblue',
    offwhite: 'white', ivory: 'white',
    charcoal: 'black', ink: 'black'
  };
  function normColour(v) {
    if (v === null || v === undefined) return '';
    var k = String(v).toLowerCase().replace(/[^a-z0-9]+/g, '');
    return COLOUR_ALIASES[k] || k;
  }
  function sameColour(a, b) {
    return normColour(a) === normColour(b);
  }

  function readVariants(card) {
    var node = card.querySelector('[data-cx-variants]');
    if (!node) return [];
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return [];
    }
  }

  function setMessage(card, text, state) {
    var msg = card.querySelector('[data-cx-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    if (state) {
      msg.dataset.state = state;
    } else {
      delete msg.dataset.state;
    }
  }

  function currentColour(card) {
    var active = card.querySelector('.cx-swatch[aria-pressed="true"]');
    return active ? active.dataset.cxColour : null;
  }

  function currentSize(card) {
    var active = card.querySelector('.cx-size[aria-pressed="true"]');
    return active ? active.dataset.cxSize : null;
  }

  function matchVariant(card, colour, size) {
    return readVariants(card).filter(function (v) {
      if (colour && !sameColour(v.colour, colour)) return false;
      if (size && String(v.size).trim() !== String(size).trim()) return false;
      return true;
    })[0];
  }

  // Swap the card image and re-check which sizes are in stock for this colour.
  function applyColour(card, colour) {
    var variants = readVariants(card);

    card.querySelectorAll('.cx-swatch').forEach(function (sw) {
      sw.setAttribute('aria-pressed', String(sameColour(sw.dataset.cxColour, colour)));
    });

    var withImage = variants.filter(function (v) {
      return sameColour(v.colour, colour) && v.image;
    })[0];
    var main = card.querySelector('.cx-card__img--main');
    if (withImage && main) {
      main.src = withImage.image;
      main.srcset = '';
    }

    card.querySelectorAll('.cx-size').forEach(function (btn) {
      var v = matchVariant(card, colour, btn.dataset.cxSize);
      var sellable = !!(v && v.available);
      btn.disabled = !sellable;
      btn.title = sellable ? '' : 'Sold out in this colour';
      if (!sellable && btn.getAttribute('aria-pressed') === 'true') {
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    setMessage(card, '');
  }

  function addToCart(card, variantId) {
    var btn = card.querySelector('[data-cx-add]');
    card.classList.add('is-busy');
    if (btn) btn.disabled = true;
    setMessage(card, 'Adding…');

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add-failed');
        return res.json();
      })
      .then(function () {
        setMessage(card, 'Added to your cart');
        refreshCart();
      })
      .catch(function () {
        // Hard fallback: send them to the product page rather than fail silently.
        setMessage(card, 'Could not add — opening the product page…', 'error');
        var link = card.querySelector('[data-cx-url]');
        if (link) window.location.href = link.dataset.cxUrl;
      })
      .then(function () {
        card.classList.remove('is-busy');
        if (btn) btn.disabled = false;
      });
  }

  // Delegate to the cart layer, which owns the drawer.
  function refreshCart() {
    if (window.CXCart) {
      window.CXCart.refresh(true).then(function () { window.CXCart.open(); });
      return;
    }
    // Cart layer missing: at least keep the badge honest.
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var fresh = new DOMParser()
          .parseFromString(d['cart-icon-bubble'], 'text/html')
          .querySelector('#cart-icon-bubble');
        if (fresh) bubble.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  function initCards(root) {
    root.querySelectorAll('[data-cx-card]').forEach(function (card) {
      if (card.dataset.cxCardReady) return;
      card.dataset.cxCardReady = '1';

      card.querySelectorAll('.cx-swatch').forEach(function (sw) {
        sw.addEventListener('click', function () {
          applyColour(card, sw.dataset.cxColour);
        });
      });

      card.querySelectorAll('.cx-size').forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          var on = btn.getAttribute('aria-pressed') === 'true';
          card.querySelectorAll('.cx-size').forEach(function (o) {
            o.setAttribute('aria-pressed', 'false');
          });
          btn.setAttribute('aria-pressed', String(!on));
          setMessage(card, '');
        });
      });

      var add = card.querySelector('[data-cx-add]');
      if (add) {
        add.addEventListener('click', function () {
          var colour = currentColour(card);
          var size = currentSize(card);

          if (!size) {
            setMessage(card, 'Pick a size first', 'error');
            return;
          }
          var variant = matchVariant(card, colour, size);
          if (!variant || !variant.available) {
            setMessage(card, 'That size is sold out', 'error');
            return;
          }
          addToCart(card, variant.id);
        });
      }

      var initial = currentColour(card);
      if (initial) applyColour(card, initial);
    });
  }

  // The card sits inside a wrapper div in the grid; hiding the card alone
  // would leave an empty cell, so walk up to the actual grid child.
  function gridCell(el) {
    var n = el;
    while (n && n.parentElement && !n.parentElement.classList.contains('cx-grid')) {
      n = n.parentElement;
    }
    return n || el;
  }

  /* ---------- 5. Colourway rail filters the showcase ---------- */

  // A store can express colour three ways: a variant option, one product per
  // colourway (colour in the title/handle), or tags. Test all three so the
  // rail works without knowing which architecture the store uses.
  function cardHasColour(card, colour) {
    var want = normColour(colour);
    if (!want) return false;

    var byVariant = readVariants(card).some(function (v) {
      return sameColour(v.colour, colour);
    });
    if (byVariant) return true;

    var hay = [
      card.dataset.cxTitle || '',
      card.dataset.cxHandle || '',
      card.dataset.cxTags || ''
    ].join(' ');
    // Compare on normalised tokens so "Sky Blue" matches "sky-blue".
    return normColour(hay).indexOf(want) !== -1;
  }

  // Last resort when a colour exists in the catalogue but not on this page:
  // send the shopper to a real filtered listing rather than doing nothing.
  function colourUrl(colour) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + 'search?q=' + encodeURIComponent(colour) + '&type=product';
  }

  function railNote(rail, text) {
    var note = rail.parentElement && rail.parentElement.querySelector('[data-cx-rail-note]');
    if (!note) return;
    note.textContent = text || '';
    note.hidden = !text;
  }

  // Dim any chip whose colour no product on the page actually stocks, so a
  // mistyped block is visible at a glance instead of failing on click.
  function markChipAvailability(rail) {
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-cx-card]'));
    if (!cards.length) return; // no cards on this page — leave chips alone
    rail.querySelectorAll('.cx-chip[data-cx-colour]').forEach(function (chip) {
      var ok = cards.some(function (card) {
        return cardHasColour(card, chip.dataset.cxColour);
      });
      chip.classList.toggle('is-empty', !ok);
      chip.title = ok ? '' : 'Not on this page — opens a search';
    });
  }

  // Add ?cxdebug=1 to the URL to print exactly what the rail can see.
  function railDebug(rail) {
    if (window.location.search.indexOf('cxdebug') === -1) return;
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-cx-card]'));
    console.log('[cx] cards on page:', cards.length);
    cards.forEach(function (card) {
      var vs = readVariants(card);
      console.log('[cx] card:', card.dataset.cxTitle, {
        handle: card.dataset.cxHandle,
        variantColours: vs.map(function (v) { return v.colour; }),
        variantSizes: vs.map(function (v) { return v.size; }),
        tags: card.dataset.cxTags
      });
    });
    rail.querySelectorAll('.cx-chip[data-cx-colour]').forEach(function (chip) {
      var c = chip.dataset.cxColour;
      console.log('[cx] chip:', c, '-> normalised:', normColour(c), '| matches:',
        cards.filter(function (card) { return cardHasColour(card, c); }).length);
    });
  }

  function initRail(root) {
    root.querySelectorAll('[data-cx-rail]').forEach(function (rail) {
      if (rail.dataset.cxRailReady) return;
      rail.dataset.cxRailReady = '1';

      markChipAvailability(rail);
      railDebug(rail);

      rail.querySelectorAll('.cx-chip[data-cx-colour]').forEach(function (chip) {
        chip.addEventListener('click', function () {
          var colour = chip.dataset.cxColour;
          var isOn = chip.getAttribute('aria-pressed') === 'true';

          rail.querySelectorAll('.cx-chip').forEach(function (c) {
            c.setAttribute('aria-pressed', 'false');
          });

          // Clicking the active chip clears the filter.
          if (isOn) {
            railNote(rail, '');
            document.querySelectorAll('[data-cx-card]').forEach(function (card) {
              gridCell(card).hidden = false;
            });
            return;
          }

          chip.setAttribute('aria-pressed', 'true');

          var cards = Array.prototype.slice.call(
            document.querySelectorAll('[data-cx-card]')
          );
          var matches = cards.filter(function (card) {
            return cardHasColour(card, colour);
          });

          // Nothing on this page carries the colour — usually because each
          // colourway is its own product and only two are in the showcase.
          // Send them to a real listing rather than blanking the grid.
          if (!matches.length) {
            chip.setAttribute('aria-pressed', 'false');
            cards.forEach(function (card) { gridCell(card).hidden = false; });
            window.location.href = colourUrl(colour);
            return;
          }

          railNote(rail, '');
          cards.forEach(function (card) {
            var has = matches.indexOf(card) !== -1;
            gridCell(card).hidden = !has;
            if (has) applyColour(card, colour);
          });

          var target = document.querySelector('[data-cx-showcase]');
          if (target) {
            target.scrollIntoView({
              behavior: reduceMotion ? 'auto' : 'smooth',
              block: 'start'
            });
          }
        });
      });
    });
  }

  /* ---------- 6. Collection sort submits on change ---------- */
  function initSort(root) {
    root.querySelectorAll('[data-cx-sort]').forEach(function (sel) {
      if (sel.dataset.cxSortReady) return;
      sel.dataset.cxSortReady = '1';
      sel.addEventListener('change', function () {
        var form = sel.closest('form');
        if (form) form.submit();
      });
    });
  }


  /* ---------- 7. Overlay hero: header floats on the artwork ----------
     Dawn sets `.section-header { position: sticky }` for a Safari z-index
     fix, which makes it a containing block — so styling the child from CSS
     could never lift the header out of page flow. We take ownership of the
     section wrapper itself and drive it with classes set here, so nothing
     in Dawn or our own stylesheets can outrank it. */
  function initOverlayHero() {
    var header = document.querySelector('.section-header');
    if (!header) return;

    // Re-query the hero every time. Shopify replaces the section element
    // whenever a setting changes in the editor, so a reference captured once
    // goes stale: the detached node reports zero height, the "scrolled past
    // the hero" test is instantly true, and the header sticks in its solid
    // state for good. That is what made changing the image look like a break.
    function currentHero() {
      return document.querySelector('.cx-hero--overlay');
    }

    function sync() {
      var hero = currentHero();
      var head = document.querySelector('.section-header');
      if (!head) return;

      if (!hero) {
        // No overlay hero on this page (or it was just switched off).
        document.body.classList.remove('cx-has-overlay-hero');
        head.classList.remove('cx-overlay-header', 'cx-overlay-solid');
        return;
      }

      document.body.classList.add('cx-has-overlay-hero');
      head.classList.add('cx-overlay-header');

      // Pale artwork gets dark header chrome instead of cream. Read from the
      // hero every sync so flipping the setting in the editor updates live.
      head.classList.toggle('cx-overlay-ink', hero.classList.contains('cx-hero--chrome-ink'));

      // Pull the hero up so the artwork starts at the very top of the page.
      // Dawn's announcement bar stays in flow; the header floats over the art.
      hero.style.marginTop = '0px';

      // Position the floating header explicitly under the announcement bar.
      // Relying on the static position left it ~20px high on mobile, so the
      // logo was clipped by the gold bar. Measuring is exact and self-corrects
      // on rotate, resize, and when the bar's message length changes.
      var bar = document.querySelector('.utility-bar');
      var barH = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--cx-utility-h', barH + 'px');

      var heroBottom = hero.getBoundingClientRect().bottom;
      var headH = head.offsetHeight || 0;
      // Guard against a zero-height hero (image still decoding): never flip
      // to solid unless we have a real measurement to act on.
      if (hero.offsetHeight < 40) {
        head.classList.remove('cx-overlay-solid');
        return;
      }
      head.classList.toggle('cx-overlay-solid', heroBottom <= headH);
    }

    if (!header.dataset.cxOverlayBound) {
      header.dataset.cxOverlayBound = '1';
      window.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      window.addEventListener('load', sync);

      // The theme editor swaps sections in and out live.
      document.addEventListener('shopify:section:load', sync);
      document.addEventListener('shopify:section:unload', sync);
      document.addEventListener('shopify:section:select', sync);
      document.addEventListener('shopify:section:reorder', sync);

      // Images finishing decode changes the hero height.
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(sync);
        var h = currentHero();
        if (h) ro.observe(h);
        document.addEventListener('shopify:section:load', function () {
          var nh = currentHero();
          if (nh) { try { ro.disconnect(); } catch (e) {} ro.observe(nh); }
        });
      }
    }

    sync();
  }

  /* ---------- boot ---------- */
  function boot(root) {
    root = root || document;
    initMarquee(root);
    initAccordion(root);
    initCards(root);   // behaviour first — never let a visual pass claim the node
    initRail(root);
    initReveal(root);
    initSort(root);
    initOverlayHero();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  // Re-run after a theme-editor section reload so the editor stays live.
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();
